import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import { Mail, Linkedin, Github, MapPin, ArrowRight } from 'lucide-react';

const Contact = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isWithinHours, setIsWithinHours] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "ditikrushna.nit@gmail.com",
      color: "from-blue-500 to-cyan-500",
      link: "mailto:ditikrushna.nit@gmail.com",
      description: "Drop me a line anytime"
    },
    {
      icon: Linkedin,
      title: "LinkedIn",
      value: "linkedin.com/in/ditikrushnagiri",
      color: "from-blue-600 to-blue-700",
      link: "https://linkedin.com/in/ditikrushnagiri",
      description: "Connect professionally"
    },
    {
      icon: Github,
      title: "GitHub",
      value: "github.com/ditikrushna",
      color: "from-gray-700 to-gray-900",
      link: "https://github.com/ditikrushna",
      description: "Check out my code"
    },
    {
      icon: MapPin,
      title: "Location",
      value: "India",
      color: "from-green-500 to-emerald-500",
      link: "https://maps.google.com/?q=India",
      description: "Based in India"
    }
  ];

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="relative mb-20 group">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 via-blue-500/10 to-cyan-500/10 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
            <div className="relative bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative flex flex-col items-center text-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                  <div className="relative p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl mb-6 transform group-hover:scale-110 transition-all duration-500">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4 transform group-hover:scale-105 transition-transform duration-500">
                  Let's Connect
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl transform group-hover:translate-y-1 transition-transform duration-500">
                  I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Cards Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl transition-all duration-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform group-hover:scale-110"></div>
                <div className="relative flex items-start gap-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className={`relative p-3 rounded-xl bg-gradient-to-br ${info.color} transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-500">
                      {info.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-1 transform group-hover:translate-x-1 transition-transform duration-500">
                      {info.value}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 transform group-hover:translate-x-2 transition-transform duration-500">
                      {info.description}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-500 transform group-hover:translate-x-2 transition-all duration-500" />
                </div>
              </a>
            ))}
          </div>

          {/* Simplified Response Time Section */}
          <div className="mt-12 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50 group hover:shadow-xl transition-all duration-500">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 transform group-hover:translate-x-1 transition-transform duration-500">
                Response Time
              </h2>
              <p className="text-gray-600 dark:text-gray-400 transform group-hover:translate-x-2 transition-transform duration-500">
                Usually within 24 hours
              </p>
            </div>
          </div>
        </div>
      </main>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }

          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }

          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }

          .animate-float {
            animation: float 3s ease-in-out infinite;
          }

          .animate-pulse-slow {
            animation: pulse 3s ease-in-out infinite;
          }

          .animate-shimmer {
            animation: shimmer 2s linear infinite;
            background: linear-gradient(
              to right,
              transparent 0%,
              rgba(255, 255, 255, 0.1) 50%,
              transparent 100%
            );
            background-size: 1000px 100%;
          }
        `}
      </style>
    </div>
  );
};

export default Contact;
